import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Smartphone, Share } from "lucide-react";

const safeStorage = {
  get(key) {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  set(key, value) {
    try { localStorage.setItem(key, value); return true; } catch { return false; }
  },
};

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function supportsInstallPrompt() {
  // Chrome, Edge, Samsung Internet on Android
  return 'onbeforeinstallprompt' in window || 'BeforeInstallPromptEvent' in window;
}

/**
 * PWAInstallPrompt — restaurant-branded install banner.
 * Android: shows native install prompt via beforeinstallprompt.
 * iOS Safari: shows manual "Add to Home Screen" instructions.
 * Other browsers: shows a generic instruction fallback.
 */
export function PWAInstallPrompt({ restaurantName, themeColor = "#059669" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [platform, setPlatform] = useState("unknown"); // "android" | "ios" | "desktop" | "unsupported"

  useEffect(() => {
    if (isStandaloneMode()) {
      console.log('[PWA_INSTALL_READY] App already in standalone mode — banner hidden');
      return;
    }

    const dismissed = safeStorage.get('pwa_install_dismissed');
    if (dismissed) {
      const daysSince = (Date.now() - new Date(dismissed).getTime()) / 86400000;
      if (daysSince < 7) return;
    }

    const ios = isIOS();
    if (ios) {
      setPlatform("ios");
      setTimeout(() => {
        console.log('[PWA_INSTALL_READY] iOS detected — showing manual instructions');
        setShowBanner(true);
      }, 3000);
      return;
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      const ua = navigator.userAgent.toLowerCase();
      const detected = /android/.test(ua) ? "android" : "desktop";
      setPlatform(detected);
      setShowBanner(true);
      console.log('[PWA_INSTALL_READY] beforeinstallprompt captured, platform:', detected);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Timeout fallback: if no prompt in 5s and browser technically could support it, show info
    const fallbackTimer = setTimeout(() => {
      if (!deferredPrompt) {
        console.log('[PWA_INSTALL_UNSUPPORTED] No beforeinstallprompt after 5s — possible unsupported browser');
      }
    }, 5000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      clearTimeout(fallbackTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInstall = async () => {
    console.log('[PWA_INSTALL_CLICK] Install button clicked, platform:', platform);
    if (!deferredPrompt) {
      console.log('[PWA_INSTALL_UNSUPPORTED] deferredPrompt not available');
      return;
    }
    try {
      deferredPrompt.prompt();
      console.log('[PWA_INSTALL_PROMPT_SHOWN] Native install prompt displayed');
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('[PWA_INSTALL_ACCEPTED] User accepted install');
        setShowBanner(false);
        safeStorage.set('pwa_installed', 'true');
      } else {
        console.log('[PWA_INSTALL_DISMISSED] User dismissed install prompt');
      }
    } catch (err) {
      console.warn('[PWA_INSTALL_UNSUPPORTED] prompt() failed:', err);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    safeStorage.set('pwa_install_dismissed', new Date().toISOString());
    console.log('[PWA_INSTALL_DISMISSED] Banner dismissed by user');
  };

  if (isStandaloneMode() || !showBanner) return null;

  // iOS — manual instructions (Safari doesn't support beforeinstallprompt)
  if (platform === "ios") {
    return (
      <div
        className="fixed bottom-20 left-3 right-3 z-50 rounded-xl shadow-2xl border overflow-hidden animate-in slide-in-from-bottom-5"
        style={{ backgroundColor: '#ffffff' }}
        data-testid="pwa-install-banner-ios"
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${themeColor}15` }}
            >
              <Smartphone className="w-6 h-6" style={{ color: themeColor }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-gray-900">
                Instalar {restaurantName || 'App'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Adicione à tela inicial para acesso rápido
              </p>
            </div>
            <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-3 p-3 bg-blue-50 rounded-lg space-y-1.5">
            <p className="text-xs font-semibold text-blue-800">Para instalar no iPhone / iPad:</p>
            <p className="text-xs text-gray-700 flex items-center gap-2">
              <span className="font-medium text-blue-700">1.</span>
              Toque em <Share className="w-4 h-4 text-blue-500 inline" /> (Compartilhar) na barra do Safari
            </p>
            <p className="text-xs text-gray-700 flex items-center gap-2">
              <span className="font-medium text-blue-700">2.</span>
              Selecione <span className="font-semibold">"Adicionar à Tela de Início"</span>
            </p>
            <p className="text-xs text-gray-500 italic mt-1">
              O Safari não permite instalação automática — este passo manual é necessário.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Android / Desktop Chrome — native install prompt available
  return (
    <div
      className="fixed bottom-20 left-3 right-3 z-50 rounded-xl shadow-2xl border overflow-hidden animate-in slide-in-from-bottom-5"
      style={{ backgroundColor: '#ffffff' }}
      data-testid="pwa-install-banner"
    >
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${themeColor}15` }}
          >
            <Download className="w-6 h-6" style={{ color: themeColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm text-gray-900">
              Instalar {restaurantName || 'App'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Acesso rápido, funciona offline!
            </p>
          </div>
          <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex gap-2 mt-3">
          <Button
            onClick={handleInstall}
            className="flex-1 h-10 text-white font-bold"
            style={{ backgroundColor: themeColor }}
            data-testid="pwa-install-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Instalar App
          </Button>
          <Button onClick={handleDismiss} variant="outline" className="h-10 px-4">
            Agora não
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to register the menu service worker.
 * Uses static /menu-sw.js (same-origin) — cross-origin SW is not allowed by browsers.
 * The heavy registration is already done in index.html; this hook is a no-op guard.
 */
export function usePWAServiceWorker(restaurantId) {
  useEffect(() => {
    if (!restaurantId || !('serviceWorker' in navigator)) return;

    // index.html already registers /menu-sw.js on page load.
    // This hook only logs confirmation that the SW is active.
    navigator.serviceWorker.ready.then((reg) => {
      console.log('[PWA_SW_REGISTERED] SW ready for menu, scope:', reg.scope);
    }).catch((err) => {
      console.warn('[PWA_SW_ERROR] SW not ready:', err);
    });
  }, [restaurantId]);
}

/**
 * Hook to dynamically inject restaurant-specific manifest link.
 * Cross-origin manifests are allowed by browsers (unlike cross-origin SW).
 */
export function usePWAManifest(restaurantId) {
  useEffect(() => {
    if (!restaurantId) return;

    // Derive API base from env or default
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';

    let link = document.querySelector('link[rel="manifest"][data-restaurant]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      link.setAttribute('data-restaurant', restaurantId);
      document.head.appendChild(link);
    }
    link.setAttribute('data-restaurant', restaurantId);
    link.href = `${apiBase}/api/public/pwa/manifest/${restaurantId}`;

    console.log('[PWA_MANIFEST_ERROR]'.replace('ERROR', 'READY'), 'Manifest loaded for:', restaurantId);

    return () => {
      if (link && link.parentNode) link.parentNode.removeChild(link);
    };
  }, [restaurantId]);
}

export default PWAInstallPrompt;
